import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import {
  adAccountDtoSchema,
  adCampaignDtoSchema,
  adRefreshDtoSchema,
  adRemovalPreviewDtoSchema,
  adSignInDoneDtoSchema,
  adSignInStartDtoSchema,
  adsSetupDtoSchema,
  type AdAccountDto,
  type AdCampaignDto,
  type AdRefreshDto,
  type AdRemovalPreviewDto,
  type AdSignInDoneDto,
  type AdSignInStartDto,
  type AdsSetupDto,
} from '@lib/dto/ads.dto'
import type {
  FinishAdSignInInput,
  RefreshAdsInput,
  StartAdSignInInput,
  UpdateAdAccountInput,
  UpdateAdCampaignInput,
} from '@lib/input/ads.input'
import type { IAdsService } from '@/types/AdsService.types'
import {
  companyAdsDtoSchema,
  venueAdsDtoSchema,
  venueAdsPeriodDtoSchema,
  type CompanyAdsDto,
  type VenueAdsDto,
  type VenueAdsPeriodDto,
} from '@lib/dto/ad-numbers.dto'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type { LeadStatsView } from '@lib/enums/lead-stats-view.enum'

/** The company's Ads page (docs/ad-analytics.spec.md): connecting Google Ads and what it reaches. */
class AdsServiceImpl implements IAdsService {
  async setup(): Promise<AdsSetupDto> {
    try {
      return await api.get('/ads/setup', adsSetupDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the company’s ads set-up', {}, error)
      throw error
    }
  }

  async startSignIn(input: StartAdSignInInput): Promise<AdSignInStartDto> {
    try {
      return await api.post('/ads/google/sign-in', input, adSignInStartDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start a Google sign-in', { venueId: input.venueId ?? null }, error)
      throw error
    }
  }

  async finishSignIn(input: FinishAdSignInInput): Promise<AdSignInDoneDto> {
    try {
      return await api.post('/ads/google/connections', input, adSignInDoneDtoSchema)
    } catch (error) {
      // Never the code or the state: they are one-time credentials.
      logRequestFailure('Failed to finish a Google sign-in', {}, error)
      throw error
    }
  }

  async removeConnection(connectionId: string): Promise<void> {
    try {
      await api.del(`/ads/connections/${connectionId}`)
    } catch (error) {
      logRequestFailure('Failed to remove an ads connection', { connectionId }, error)
      throw error
    }
  }

  async updateAccount(accountId: string, input: UpdateAdAccountInput): Promise<AdAccountDto> {
    try {
      return await api.put(`/ads/accounts/${accountId}`, input, adAccountDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change an ad account', { accountId, action: input.action }, error)
      throw error
    }
  }

  async removalPreview(accountId: string): Promise<AdRemovalPreviewDto> {
    try {
      return await api.get(`/ads/accounts/${accountId}/removal`, adRemovalPreviewDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load what removing an ad account would delete', { accountId }, error)
      throw error
    }
  }

  async removeFromVenue(accountId: string): Promise<void> {
    try {
      await api.del(`/ads/accounts/${accountId}/use`)
    } catch (error) {
      logRequestFailure('Failed to remove an ad account from its venue', { accountId }, error)
      throw error
    }
  }

  async updateCampaign(campaignId: string, input: UpdateAdCampaignInput): Promise<AdCampaignDto> {
    try {
      return await api.put(`/ads/campaigns/${campaignId}`, input, adCampaignDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to change where a campaign counts', { campaignId }, error)
      throw error
    }
  }

  async refresh(input: RefreshAdsInput): Promise<AdRefreshDto> {
    try {
      return await api.post('/ads/refresh', input, adRefreshDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to start reading Google Ads again', { venueId: input.venueId ?? null }, error)
      throw error
    }
  }

  async company(period: CalendarDay | null): Promise<CompanyAdsDto> {
    try {
      return await api.get(period === null ? '/ads/summary' : `/ads/summary?period=${period}`, companyAdsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the company’s ad numbers', { period }, error)
      throw error
    }
  }

  async venue(venueId: string): Promise<VenueAdsDto> {
    try {
      return await api.get(`/venues/${venueId}/ads`, venueAdsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a venue’s ad numbers', { venueId }, error)
      throw error
    }
  }

  async venuePeriod(venueId: string, start: CalendarDay, view: LeadStatsView = 'cohort'): Promise<VenueAdsPeriodDto> {
    try {
      return await api.get(`/venues/${venueId}/ads/periods/${start}?view=${view}`, venueAdsPeriodDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load a period of a venue’s ad numbers', { venueId, start }, error)
      throw error
    }
  }
}

export const adsService: IAdsService = new AdsServiceImpl()
